import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { SITE, MODELS } from "@/data/content";

const initial = {
  ime_prezime: "",
  telefon: "",
  email: "",
  tip_kontejnera: "",
  poruka: "",
  website: "", // honeypot
};


const CONFIG_STORAGE_KEY = "rbtim_configuration_request";

const drawingLabels = {
  open: "otvoren raspored",
  living: "stambeni raspored",
  office: "kancelarijski raspored",
  storage: "magacinski / pomoćni raspored",
  workshop: "radionički raspored",
  guard: "portirnica",
  counter: "portirnica sa šalterom",
  doubleOffice: "podeljen / dupli modul",
  "controlled-builder-v2": "kontrolisana skica konfiguratora",
  wc: "raspored sa WC blokom",
  bath: "raspored sa kupatilom",
  smallWc: "mali WC modul",
  showers: "tuš kabine",
  multiSanitary: "sanitarni raspored",
};

const formatConfigurationForMessage = (configuration) => {
  if (!configuration) return "";

  const options = Array.isArray(configuration.selectedOptions) && configuration.selectedOptions.length
    ? configuration.selectedOptions.join(", ")
    : "bez dodatnih opcija";

  const drawing = configuration.drawing || {};
  const drawingType = drawingLabels[drawing.layoutType] || drawing.layoutType || "informativni crtež";
  const door = drawing.hasDoor ? "da" : "ne";
  const windowValue = drawing.hasWindow ? "da" : "ne";

  const builderLines = Array.isArray(configuration.builderSummary) && configuration.builderSummary.length
    ? ["", "Detalji skice:", ...configuration.builderSummary.map((line) => `- ${line}`)]
    : [];

  const attachmentType = configuration.drawingAttachment?.mimeType === "image/png"
    ? "PNG slika"
    : configuration.drawingAttachment?.svg
      ? "SVG fajl"
      : configuration.drawingAttachment?.dataUrl
        ? "slika"
        : "nije priložen kao fajl";

  return [
    "Konfiguracija poslata sa sajta:",
    `Model / osnova: ${configuration.model || "nije definisano"}`,
    `Dimenzije: ${configuration.dimensions || drawing.dimensions || "nije definisano"}`,
    `Crtež / raspored: ${drawingType}`,
    `Vrata na crtežu: ${door}`,
    `Prozor na crtežu: ${windowValue}`,
    `Izabrane opcije: ${options}`,
    `Crtež: priložen kao ${attachmentType} u emailu`,
    `Polazna stranica: ${configuration.sourcePath || "/"}`,
    ...builderLines,
    "",
    "Dodatna poruka kupca:",
  ].join("\n");
};

const getModelTitleFromConfiguration = (configuration) => {
  if (!configuration) return "";

  const matchedModel = MODELS.find((model) => {
    return (
      model.key === configuration.modelPage ||
      configuration.sourcePath?.includes(model.slug) ||
      configuration.model?.toLowerCase().includes(model.title.toLowerCase().replace(" kontejneri", ""))
    );
  });

  return matchedModel?.title || "Ostalo";
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [configuration, setConfiguration] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: null, msg: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const cameFromConfigurator = params.get("konfiguracija") === "1";
      const savedConfiguration = window.localStorage.getItem(CONFIG_STORAGE_KEY);

      if (!cameFromConfigurator || !savedConfiguration) return;

      const parsedConfiguration = JSON.parse(savedConfiguration);
      const configurationMessage = formatConfigurationForMessage(parsedConfiguration);
      const modelTitle = getModelTitleFromConfiguration(parsedConfiguration);

      setConfiguration(parsedConfiguration);
      setForm((currentForm) => ({
        ...currentForm,
        tip_kontejnera: currentForm.tip_kontejnera || modelTitle,
        poruka: currentForm.poruka || configurationMessage,
      }));
    } catch (error) {
      console.warn("Konfiguracija iz browsera nije mogla da se pročita.", error);
    }
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;

    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: undefined,
    }));
  };

  const validate = () => {
    const validationErrors = {};

    if (!form.ime_prezime.trim() || form.ime_prezime.trim().length < 2) {
      validationErrors.ime_prezime = "Unesite vaše ime i prezime.";
    }

    if (!form.telefon.trim() || form.telefon.trim().length < 4) {
      validationErrors.telefon = "Unesite broj telefona.";
    }

    if (
      form.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    ) {
      validationErrors.email = "Unesite ispravan email ili ostavite polje prazno.";
    }

    if (!form.tip_kontejnera.trim()) {
      validationErrors.tip_kontejnera = "Izaberite tip kontejnera.";
    }

    if (!form.poruka.trim() || form.poruka.trim().length < 2) {
      validationErrors.poruka = "Napišite kratku poruku.";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    setStatus({ type: null, msg: "" });

    if (!validate()) return;

    setSubmitting(true);

    try {
      const payload = {
        ...form,
        source_page: configuration?.sourcePath || window.location.pathname,
        configuration,
      };

      const endpoint = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
        ? "https://rbtim.com/send-mail.php"
        : "/send-mail.php";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const raw = await response.text();
      let result;

      try {
        result = JSON.parse(raw);
      } catch (parseError) {
        throw new Error(
          "Server nije vratio JSON odgovor. Proveri da li je send-mail.php uploadovan u root sajta i da li PHP radi na hostingu."
        );
      }

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Greška pri slanju upita.");
      }

      setStatus({
        type: "ok",
        msg: result.message || "Hvala! Vaš upit je poslat. Javićemo vam se uskoro.",
      });

      try {
        window.localStorage.removeItem(CONFIG_STORAGE_KEY);
      } catch (error) {
        console.warn("Konfiguracija nije mogla da se ukloni iz browsera.", error);
      }

      setConfiguration(null);
      setForm(initial);
    } catch (err) {
      setStatus({
        type: "err",
        msg:
          err.message ||
          "Greška pri slanju upita. Pokušajte ponovo ili nas pozovite direktno.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO />

      <PageHero
        crumbs={[{ label: "Početna", to: "/" }, { label: "Kontakt" }]}
        title="Kontakt"
        lead="Pošaljite nam upit. Recite nam za koju namenu vam je potreban kontejner i pripremićemo ponudu."
      />

      <section className="section" data-testid="contact-page">
        <div className="container-rb contact-grid">
          <aside className="contact-info">
            <div>
              <h3 style={{ marginTop: 0, marginBottom: 6 }}>
                Kontakt informacije
              </h3>
              <p style={{ color: "#6b7280", margin: 0 }}>
                Tu smo da odgovorimo na vaša pitanja i pripremimo ponudu.
              </p>
            </div>

            <div className="contact-info__item">
              <div className="contact-info__icon">
                <Phone size={18} />
              </div>
              <div>
                <div className="contact-info__label">Telefon</div>
                <a
                  className="contact-info__value"
                  href={SITE.phoneHref}
                  data-testid="contact-phone"
                >
                  {SITE.phone}
                </a>
              </div>
            </div>

            <div className="contact-info__item">
              <div className="contact-info__icon">
                <Mail size={18} />
              </div>
              <div>
                <div className="contact-info__label">Email</div>
                <a
                  className="contact-info__value"
                  href={SITE.emailHref}
                  data-testid="contact-email"
                >
                  {SITE.email}
                </a>
              </div>
            </div>

            <div className="contact-info__item">
              <div className="contact-info__icon">
                <MapPin size={18} />
              </div>
              <div>
                <div className="contact-info__label">Adresa</div>
                <span
                  className="contact-info__value"
                  data-testid="contact-address"
                >
                  {SITE.fullAddress}
                </span>
              </div>
            </div>

            <div className="contact-info__item">
              <div className="contact-info__icon">
                <Clock size={18} />
              </div>
              <div>
                <div className="contact-info__label">Radno vreme</div>
                {SITE.hours.map((h) => (
                  <div
                    key={h.day}
                    style={{
                      fontSize: "0.98rem",
                      color: "#374151",
                      fontWeight: 500,
                    }}
                  >
                    <span style={{ color: "#6b7280" }}>{h.day}:</span>{" "}
                    <strong style={{ color: "#111827" }}>{h.value}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                borderTop: "1px solid #eef0f3",
                paddingTop: 18,
                color: "#6b7280",
                fontSize: ".94rem",
              }}
            >
              Radimo standardne kontejnere za stanovanje, kancelarije,
              gradilišta, sanitarne potrebe, portirnice i pomoćne objekte.
            </div>
          </aside>

          <form
            className="form"
            onSubmit={onSubmit}
            noValidate
            data-testid="contact-form"
          >
            <h3 style={{ marginTop: 0 }}>Pošaljite upit</h3>
            <p style={{ marginTop: -8, marginBottom: 4, color: "#6b7280" }}>
              Unesite osnovne podatke i kratko opišite šta vam je potrebno.
            </p>

            {configuration && (
              <div className="configuration-transfer-card" data-testid="configuration-transfer-card">
                <div>
                  <span>Konfiguracija učitana</span>
                  <strong>{configuration.model}</strong>
                  <small>{configuration.dimensions}</small>
                </div>
                <div>
                  <span>Opcije</span>
                  <strong>
                    {Array.isArray(configuration.selectedOptions) && configuration.selectedOptions.length
                      ? configuration.selectedOptions.join(" • ")
                      : "Bez dodatnih opcija"}
                  </strong>
                  <small>{configuration.drawingAttachment?.mimeType === "image/png" ? "Crtež će biti poslat kao PNG slika u emailu." : configuration.drawingAttachment?.svg ? "Crtež će biti poslat kao SVG fajl u emailu." : "Crtež je pretvoren u tekstualni tehnički opis za email."}</small>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="field">
                <label htmlFor="ime_prezime">Ime i prezime *</label>
                <input
                  id="ime_prezime"
                  name="ime_prezime"
                  type="text"
                  value={form.ime_prezime}
                  onChange={onChange}
                  data-testid="input-ime"
                  required
                />
                {errors.ime_prezime && (
                  <span style={{ color: "#b91c1c", fontSize: 12 }}>
                    {errors.ime_prezime}
                  </span>
                )}
              </div>

              <div className="field">
                <label htmlFor="telefon">Telefon *</label>
                <input
                  id="telefon"
                  name="telefon"
                  type="tel"
                  value={form.telefon}
                  onChange={onChange}
                  data-testid="input-telefon"
                  required
                />
                {errors.telefon && (
                  <span style={{ color: "#b91c1c", fontSize: 12 }}>
                    {errors.telefon}
                  </span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid="input-email"
                />
                {errors.email && (
                  <span style={{ color: "#b91c1c", fontSize: 12 }}>
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="field">
                <label htmlFor="tip_kontejnera">Tip kontejnera *</label>
                <select
                  id="tip_kontejnera"
                  name="tip_kontejnera"
                  value={form.tip_kontejnera}
                  onChange={onChange}
                  data-testid="input-tip"
                  required
                >
                  <option value="">— Izaberite —</option>
                  {MODELS.map((m) => (
                    <option key={m.slug} value={m.title}>
                      {m.title}
                    </option>
                  ))}
                  <option value="Ostalo">Ostalo</option>
                </select>
                {errors.tip_kontejnera && (
                  <span style={{ color: "#b91c1c", fontSize: 12 }}>
                    {errors.tip_kontejnera}
                  </span>
                )}
              </div>
            </div>

            <div className="field">
              <label htmlFor="poruka">Poruka *</label>
              <textarea
                id="poruka"
                name="poruka"
                rows={5}
                value={form.poruka}
                onChange={onChange}
                data-testid="input-poruka"
                required
                placeholder="Opišite namenu, dimenzije, rok ili osnovne zahteve..."
              />
              {errors.poruka && (
                <span style={{ color: "#b91c1c", fontSize: 12 }}>
                  {errors.poruka}
                </span>
              )}
            </div>

            <div className="field field--honey" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={onChange}
              />
            </div>

            {status.type === "ok" && (
              <div
                className="form-status form-status--ok"
                data-testid="form-status-ok"
                role="status"
              >
                {status.msg}
              </div>
            )}

            {status.type === "err" && (
              <div
                className="form-status form-status--err"
                data-testid="form-status-err"
                role="alert"
              >
                {status.msg}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={submitting}
              data-testid="submit-contact"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="spin" /> Slanje...
                </>
              ) : (
                "Pošalji upit"
              )}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Contact;